import { connect, disconnect, sendMessage, socketConnected, socketDisconnected, socketError } from './socketSlice';
import { setSuggestions, upsertMessage, setProcessing, setMessagesRemaining } from './chatSlice';

const socketMiddleware = (storeAPI) => {
    const FLUSH_INTERVAL = 10;
    const maxReconnectAttempts = 5;

    let socket = null;
    let buffer = "";
    let flushTimer = null;
    let currentMessageId = null;
    let reconnectAttempts = 0;
    let reconnectTimeout = null;
    let pendingMessages = [];
    let currentUrl = null;
    let shouldReconnect = true;

    const flushBuffer = () => {
        if (buffer && currentMessageId) {
            storeAPI.dispatch(upsertMessage({
                id: currentMessageId,
                content: buffer,
                role: 'assistant'
            }));
            buffer = "";
            currentMessageId = null;
        }
        flushTimer = null;
    };

    const connectSocket = (url) => {
        currentUrl = url;
        socket = new WebSocket(url);

        socket.onopen = () => {
            reconnectAttempts = 0;
            storeAPI.dispatch(socketConnected());

            while (pendingMessages.length > 0) {
                const msg = pendingMessages.shift();
                if (socket && socket.readyState === WebSocket.OPEN) {
                    socket.send(JSON.stringify(msg));
                }
            }
        };

        socket.onmessage = (event) => {
            try {
                const messageData = JSON.parse(event.data);
                if (currentMessageId !== messageData.id) {
                    flushBuffer();
                    currentMessageId = messageData.id;
                }
                buffer += messageData.content;
                if (messageData.done) {
                    storeAPI.dispatch(setSuggestions(messageData.suggestions));
                    flushBuffer();
                    storeAPI.dispatch(setProcessing(false));
                    storeAPI.dispatch(setMessagesRemaining(messageData.messagesRemaining));
                }
                else if (!flushTimer) {
                    flushTimer = setTimeout(flushBuffer, FLUSH_INTERVAL);
                }
            } catch (error) {
                console.error('Error parsing message:', error);
            }
        };

        socket.onerror = (errorEvent) => {
            const errorInfo = { type: errorEvent.type, timestamp: Date.now() };
            storeAPI.dispatch(socketError(errorInfo));
        };

        socket.onclose = (event) => {
            clearTimeout(flushTimer);
            flushBuffer();
            storeAPI.dispatch(socketDisconnected());

            if (event.code === 4000) {
                // Unauthorized JWT detected, navigate to login
                window.location.href = '/login';
                return;
            }

            if (shouldReconnect && reconnectAttempts < maxReconnectAttempts) {
                reconnectAttempts++;
                const timeout = Math.pow(2, reconnectAttempts) * 1000;
                reconnectTimeout = setTimeout(() => {
                    connectSocket(currentUrl);
                }, timeout);
            } else if (!shouldReconnect) {
                reconnectAttempts = 0;
            }
        };
    };

    return (next) => (action) => {
        if (action.type === connect.type) {
            shouldReconnect = true;
            const { url } = action.payload;
            if (socket) {
                socket.close();
                socket = null;
            }
            connectSocket(url);
        } else if (action.type === sendMessage.type) {
            const payload = action.payload;
            if (socket && socket.readyState === WebSocket.OPEN) {
                storeAPI.dispatch(setProcessing(true));
                socket.send(JSON.stringify(payload));
            } else {
                storeAPI.dispatch(setProcessing(true));
                pendingMessages.push(payload);
            }
        } else if (action.type === disconnect.type) {
            shouldReconnect = false;
            if (socket) {
                socket.close();
                socket = null;
            }
            clearTimeout(reconnectTimeout);
            pendingMessages = [];
        }
        return next(action);
    };
};

export default socketMiddleware;
